/**
 * cart-footer.js
 *
 * AMP SlideCart footer enhancements carried forward from production.
 *
 * Owns:
 * 1. Injecting the 3-column trust row below the checkout form.
 * 2. Preserving discount-code checkout redirects used by the existing theme.
 *
 * Does NOT own:
 * - Shipping text ("FREE" / "Calculated at checkout") -> AMP
 * - Savings text / strike-through subtotal -> AMP / Shopify discount state
 * - Giftkart .gkt* wrappers -> Giftkart
 */

(() => {
  const CART_ROOT_SELECTOR = '#slidecarthq';
  const FOOTER_SELECTOR = `${CART_ROOT_SELECTOR} footer.footer.sticky-footer`;
  const CHECKOUT_FORM_SELECTOR = '#slidecart-checkout-form';
  const CHECKOUT_BUTTON_SELECTOR = `${CART_ROOT_SELECTOR} form${CHECKOUT_FORM_SELECTOR} button[name="checkout"]`;

  const TRUST_ROW_CLASS = 'js-footer-trust-row';
  const DISCOUNT_STORAGE_KEY = 'auto-discount';

  const CHECK_SVG = `
    <svg
      class="footer-trust-check"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M7.7 13.7 3.8 9.8a1 1 0 0 0-1.4 1.4l4.6 4.6a1 1 0 0 0 1.4 0l9-9a1 1 0 1 0-1.4-1.4l-8.3 8.3Z"></path>
    </svg>
  `;

  /**
   * Injects the production trust row beneath the AMP checkout form.
   * Safe to call repeatedly; exits if the row is already present.
   */
  function ensureTrustRow() {
    const footer = document.querySelector(FOOTER_SELECTOR);
    if (!footer || footer.querySelector(`.${TRUST_ROW_CLASS}`)) return;

    const form = footer.querySelector(CHECKOUT_FORM_SELECTOR);
    if (!form) return;

    const row = document.createElement('div');
    row.className = `${TRUST_ROW_CLASS} footer-trust-row`;
    row.innerHTML = `
      <div class="footer-trust-col">
        ${CHECK_SVG}
        <span class="footer-trust-text">4.7 Stars,<br>17k+ Reviews</span>
      </div>

      <div class="footer-trust-col">
        ${CHECK_SVG}
        <span class="footer-trust-text">24/7<br>Crochet Help</span>
      </div>

      <div class="footer-trust-col">
        ${CHECK_SVG}
        <span class="footer-trust-text">Shipped out<br>of the USA</span>
      </div>
    `;

    form.insertAdjacentElement('afterend', row);
  }

  /**
   * AMP can replace/re-render the footer during SLIDECART_UPDATE().
   * Watch the cart root and restore our custom footer content whenever needed.
   */
  function watchSlideCartFooter() {
    ensureTrustRow();

    const observer = new MutationObserver(() => {
      ensureTrustRow();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Existing production behavior:
   * capture discount codes from either:
   *   ?discount=CODE
   *   ?auto-discount=CODE
   *   /discount/CODE
   *
   * Persist in sessionStorage so the SlideCart checkout button can retain
   * the discount when sending the shopper to checkout.
   */
  function captureDiscountCode() {
    const params = new URLSearchParams(window.location.search);
    const queryCode = params.get('auto-discount') || params.get('discount');

    if (queryCode) {
      sessionStorage.setItem(DISCOUNT_STORAGE_KEY, queryCode);
    }

    const discountPathMatch = window.location.pathname.match(/^\/discount\/([^/]+)/);

    if (discountPathMatch?.[1]) {
      sessionStorage.setItem(DISCOUNT_STORAGE_KEY, decodeURIComponent(discountPathMatch[1]));
    }
  }

  /**
   * Delegated click handler survives AMP footer re-renders.
   * If there is no stored discount code, AMP's normal checkout behavior
   * continues untouched.
   */
  function preserveDiscountOnCheckout() {
    document.addEventListener(
      'click',
      (event) => {
        const checkoutButton = event.target?.closest?.(CHECKOUT_BUTTON_SELECTOR);
        if (!checkoutButton) return;

        const discountCode = sessionStorage.getItem(DISCOUNT_STORAGE_KEY);
        if (!discountCode) return;

        event.preventDefault();
        event.stopPropagation();

        window.location.href = `/checkout?discount=${encodeURIComponent(discountCode)}`;
      },
      true,
    );
  }

  function initCartFooter() {
    captureDiscountCode();
    preserveDiscountOnCheckout();
    watchSlideCartFooter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartFooter, { once: true });
  } else {
    initCartFooter();
  }
})();
